import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase';
import { detectPlatform, isStandalone } from '../utils/installPlatform';

const REPORT_EVERY_MS = 24 * 60 * 60 * 1000;

function lastReportedAt(uid) {
  try { return Number(localStorage.getItem(`appInstallReportedAt:${uid}`)) || 0; } catch (e) { return 0; }
}

function markReported(uid) {
  try { localStorage.setItem(`appInstallReportedAt:${uid}`, String(Date.now())); } catch (e) { /* private mode */ }
}

/**
 * Tells the server which signed-in users have the app installed (admin → Visitor Statistics → App & Alerts).
 * A visit from the installed app (standalone window) is the proof; the browser's `appinstalled`
 * event covers the moment of installing. Renders nothing.
 */
export default function AppInstallTracker() {
  useEffect(() => {
    const report = async (event) => {
      const user = auth.currentUser;
      if (!user) return;
      if (event === 'standalone' && Date.now() - lastReportedAt(user.uid) < REPORT_EVERY_MS) return;
      const p = detectPlatform();
      try {
        await httpsCallable(getFunctions(), 'reportAppInstall')({
          event,
          os: p.os,
          // the iOS Home Screen web view has no "Safari" in its user agent
          browser: p.os === 'ios' && p.browser === 'other' ? 'safari' : p.browser,
        });
        markReported(user.uid);
      } catch (e) {
        console.debug('App install report failed:', e.message);
      }
    };

    const stopAuth = onAuthStateChanged(auth, (user) => {
      if (user && isStandalone()) report('standalone');
    });
    const onInstalled = () => report('appinstalled');
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      stopAuth();
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  return null;
}
